import { useEffect, useRef } from 'react';
import {
  addProtocol,
  LayerSpecification,
  Map as MapLibreMap,
  Popup,
  setWorkerUrl,
  SourceSpecification
} from 'maplibre-gl';
import { Protocol } from 'pmtiles';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Box, Typography } from '@mui/material';
import { LocationOff } from '@mui/icons-material';
import { buildFovPolygon } from './geo';
import { YardGeometryProps } from 'api/yardGeometry';

// pmtiles ships as a custom URL scheme — maplibre needs the protocol handler
// registered globally, once, before any map using a pmtiles:// source loads.
let pmtilesProtocolRegistered = false;
function ensurePmtilesProtocol() {
  if (pmtilesProtocolRegistered) return;
  const protocol = new Protocol();
  addProtocol('pmtiles', protocol.tile);
  pmtilesProtocolRegistered = true;
}

// maplibre's own default worker resolution (relative to its module URL) isn't
// statically analyzable by webpack, so point it at the worker explicitly via
// the documented `new URL(<package request>, import.meta.url)` form — see
// https://maplibre.org/maplibre-gl-js/docs/#webpack-5. Webpack's generic
// `new URL()` asset handling only copies the referenced file byte-for-byte,
// though — it doesn't follow *its* imports — and the worker hardcodes a
// relative `import ... from './maplibre-gl-shared.mjs'`. So the shared chunk
// is referenced here too, purely so webpack's asset rule (craco.config.js)
// picks it up and emits it next to the worker at the fixed, unhashed path the
// worker's relative import expects. Both come straight from node_modules on
// every build; nothing is manually vendored.
let workerUrlSet = false;
function ensureWorkerUrl() {
  if (workerUrlSet) return;
  new URL('maplibre-gl/dist/maplibre-gl-shared.mjs', import.meta.url);
  setWorkerUrl(
    new URL('maplibre-gl/dist/maplibre-gl-worker.mjs', import.meta.url).toString()
  );
  workerUrlSet = true;
}

const PMTILES_URL =
  'https://storage.googleapis.com/shared-services-e880-rtls-map-data/map.pmtiles';

interface YardSpotMapProps {
  lat: number | null;
  lon: number | null;
  heading: number | null;
  geometry: GeoJSON.FeatureCollection<GeoJSON.Geometry, YardGeometryProps> | null;
  spotUuid: string | null;
  spotName: string | null;
  zoneName: string | null;
  /**
   * A second spot to highlight in a different color — e.g. the spot the
   * system expects a trailer at, when that disagrees with the spot it was
   * actually read at. Matched by uuid only (no name/zone fallback), and
   * skipped if it's the same spot as spotUuid/spotName+zoneName. Optional:
   * omitted entirely, the map behaves exactly as it did with one spot.
   */
  expectedSpotUuid?: string | null;
}

function isSelectedSpot(
  props: YardGeometryProps,
  spotUuid: string | null,
  spotName: string | null,
  zoneName: string | null
) {
  if (spotUuid && props.flux_spot_id) return props.flux_spot_id === spotUuid;
  return (
    spotName !== null &&
    zoneName !== null &&
    props.name === spotName &&
    props.zone === zoneName
  );
}

// Adds the yard-spot boundaries once the map style has finished loading
// (they're fetched separately over GraphQL, so they can't be baked into the
// initial style like the pmtiles/camera/fov sources are). Inserted below the
// fov/camera layers so the capture marker stays on top.
function addYardGeometryLayers(
  map: MapLibreMap,
  geometry: GeoJSON.FeatureCollection<GeoJSON.Geometry, YardGeometryProps>,
  spotUuid: string | null,
  spotName: string | null,
  zoneName: string | null,
  expectedSpotUuid: string | null
) {
  const isObserved = (props: YardGeometryProps) =>
    isSelectedSpot(props, spotUuid, spotName, zoneName);
  // Uuid-only: currentLocation carries no spot name/zone to fall back on.
  // Excludes anything already the observed spot, so the two never overlap.
  const isExpected = (props: YardGeometryProps) =>
    expectedSpotUuid !== null &&
    props.flux_spot_id === expectedSpotUuid &&
    !isObserved(props);

  const boundary = geometry.features.filter((f) => f.properties.kind === 'boundary');
  const otherSpots = geometry.features.filter(
    (f) =>
      f.properties.kind === 'parking_spot' &&
      !isObserved(f.properties) &&
      !isExpected(f.properties)
  );
  const selectedSpot = geometry.features.filter(
    (f) => f.properties.kind === 'parking_spot' && isObserved(f.properties)
  );
  const expectedSpot = geometry.features.filter(
    (f) => f.properties.kind === 'parking_spot' && isExpected(f.properties)
  );

  map.addSource('yard-boundary', {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: boundary }
  });
  map.addSource('yard-spots', {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: otherSpots }
  });
  map.addSource('yard-spot-expected', {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: expectedSpot }
  });
  map.addSource('yard-spot-selected', {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: selectedSpot }
  });

  const beforeId = map.getLayer('fov-fill') ? 'fov-fill' : 'camera-halo';
  map.addLayer(
    {
      id: 'yard-boundary-line',
      type: 'line',
      source: 'yard-boundary',
      paint: { 'line-color': '#64748b', 'line-width': 2, 'line-dasharray': [2, 1] }
    },
    beforeId
  );
  map.addLayer(
    {
      id: 'yard-spots-fill',
      type: 'fill',
      source: 'yard-spots',
      paint: { 'fill-color': '#93c5fd', 'fill-opacity': 0.25 }
    },
    beforeId
  );
  map.addLayer(
    {
      id: 'yard-spots-outline',
      type: 'line',
      source: 'yard-spots',
      paint: { 'line-color': '#3b82f6', 'line-width': 1 }
    },
    beforeId
  );
  map.addLayer(
    {
      id: 'yard-spot-expected-fill',
      type: 'fill',
      source: 'yard-spot-expected',
      paint: { 'fill-color': '#f59e0b', 'fill-opacity': 0.35 }
    },
    beforeId
  );
  map.addLayer(
    {
      id: 'yard-spot-expected-outline',
      type: 'line',
      source: 'yard-spot-expected',
      paint: { 'line-color': '#b45309', 'line-width': 2 }
    },
    beforeId
  );
  map.addLayer(
    {
      id: 'yard-spot-selected-fill',
      type: 'fill',
      source: 'yard-spot-selected',
      paint: { 'fill-color': '#22c55e', 'fill-opacity': 0.35 }
    },
    beforeId
  );
  map.addLayer(
    {
      id: 'yard-spot-selected-outline',
      type: 'line',
      source: 'yard-spot-selected',
      paint: { 'line-color': '#16a34a', 'line-width': 2 }
    },
    beforeId
  );

  addSpotHoverTooltip(map, [
    'yard-spots-fill',
    'yard-spot-expected-fill',
    'yard-spot-selected-fill'
  ]);
}

// Shows a small tooltip with the spot's zone and name while the cursor is
// over any of its fill layers (plain, expected or selected) — whichever one
// is on top at the cursor's position. A single map-wide `mousemove` handler,
// rather than one per layer, so only one tooltip/cursor state is ever live
// regardless of which layer the spot under the cursor happens to belong to.
function addSpotHoverTooltip(map: MapLibreMap, spotLayerIds: string[]) {
  const tooltip = new Popup({
    closeButton: false,
    closeOnClick: false,
    offset: 8
  });

  const label = (props: YardGeometryProps) => {
    const zone = props.zone ? `Zone ${props.zone}` : null;
    const name = props.name ?? 'Unnamed spot';
    return [zone, name].filter(Boolean).join(' · ');
  };

  map.on('mousemove', (e) => {
    const [feature] = map.queryRenderedFeatures(e.point, { layers: spotLayerIds });
    if (!feature) {
      map.getCanvas().style.cursor = '';
      tooltip.remove();
      return;
    }
    map.getCanvas().style.cursor = 'pointer';
    const content = document.createElement('div');
    content.textContent = label(feature.properties as YardGeometryProps);
    content.style.fontSize = '12px';
    tooltip.setLngLat(e.lngLat).setDOMContent(content).addTo(map);
  });

  map.on('mouseout', () => {
    map.getCanvas().style.cursor = '';
    tooltip.remove();
  });
}

function buildStyle(lat: number, lon: number, heading: number | null) {
  const sources: Record<string, SourceSpecification> = {
    site: {
      type: 'vector',
      url: `pmtiles://${PMTILES_URL}`,
      maxzoom: 18
    },
    camera: {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: { type: 'Point', coordinates: [lon, lat] }
      }
    }
  };

  const layers: LayerSpecification[] = [
    { id: 'background', type: 'background', paint: { 'background-color': '#eef2f5' } },
    {
      'id': 'parking-fill',
      'type': 'fill',
      'source': 'site',
      'source-layer': 'parking',
      'paint': { 'fill-color': '#d9dee3', 'fill-opacity': 0.8 }
    },
    {
      'id': 'buildings-fill',
      'type': 'fill',
      'source': 'site',
      'source-layer': 'buildings',
      'paint': { 'fill-color': '#c3cad1', 'fill-opacity': 0.9 }
    },
    {
      'id': 'buildings-outline',
      'type': 'line',
      'source': 'site',
      'source-layer': 'buildings',
      'paint': { 'line-color': '#9aa5b1', 'line-width': 1 }
    },
    {
      'id': 'roads-line',
      'type': 'line',
      'source': 'site',
      'source-layer': 'roads',
      'paint': { 'line-color': '#ffffff', 'line-width': 2 }
    }
  ];

  if (heading !== null) {
    sources.fov = { type: 'geojson', data: buildFovPolygon(lat, lon, heading) };
    layers.push(
      {
        id: 'fov-fill',
        type: 'fill',
        source: 'fov',
        paint: { 'fill-color': '#f97316', 'fill-opacity': 0.35 }
      },
      {
        id: 'fov-outline',
        type: 'line',
        source: 'fov',
        paint: { 'line-color': '#ea580c', 'line-width': 1.5 }
      }
    );
  }

  layers.push(
    {
      id: 'camera-halo',
      type: 'circle',
      source: 'camera',
      paint: { 'circle-radius': 8, 'circle-color': '#2563eb', 'circle-opacity': 0.25 }
    },
    {
      id: 'camera-point',
      type: 'circle',
      source: 'camera',
      paint: {
        'circle-radius': 4,
        'circle-color': '#2563eb',
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff'
      }
    }
  );

  return { version: 8 as const, sources, layers };
}

const YardSpotMap = ({
  lat,
  lon,
  heading,
  geometry,
  spotUuid,
  spotName,
  zoneName,
  expectedSpotUuid = null
}: YardSpotMapProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);

  useEffect(() => {
    if (lat === null || lon === null || !containerRef.current) return;

    ensurePmtilesProtocol();
    ensureWorkerUrl();

    const map = new MapLibreMap({
      container: containerRef.current,
      style: buildStyle(lat, lon, heading),
      center: [lon, lat],
      minZoom: 15,
      maxZoom: 18,
      attributionControl: {
        compact: true,
        customAttribution:
          '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors'
      }
    });
    mapRef.current = map;

    map.on('load', () => {
      if (geometry) {
        addYardGeometryLayers(
          map,
          geometry,
          spotUuid,
          spotName,
          zoneName,
          expectedSpotUuid
        );
      }
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [lat, lon, heading, geometry, spotUuid, spotName, zoneName, expectedSpotUuid]);

  if (lat === null || lon === null) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: 'text.disabled'
        }}
      >
        <LocationOff fontSize="large" />
        <Typography variant="caption" sx={{ mt: 0.5 }}>
          No location available
        </Typography>
      </Box>
    );
  }

  return <Box ref={containerRef} sx={{ width: '100%', height: '100%' }} />;
};

export default YardSpotMap;
